import React, { useEffect, useState } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Modal,
  Form,
  Popconfirm,
  notification,
  Row,
  Col,
} from "antd";
import BasicButtonPrimary from "components/BasicButtonPrimary";
import { useTranslation } from "react-i18next";
import AIAssistantService from "services/aiAssistantService";
import { EXTRA_EXTRA_BIG_COLUMND_WITDTH } from "constants/app";
import { MIN_COLUMN_WIDTH } from "constants/app";
import { BIG_COLUMN_WIDTH } from "constants/app";
import { useHistory } from "react-router-dom";
import { routes } from "App";
import TorichatService from "services/torichatService";

export default function TorichatProfessionalKnowledge({ merchantCode }) {
  const { t: translation } = useTranslation();
  const history = useHistory();
  const [filter, setFilter] = useState({
    merchantCode: merchantCode || "",
    searchText: "",
  });

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loadingSync, setLoadingSync] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  // Table columns
  const columns = [
    {
      title: translation("setting.ai.STT"),
      render: (_, __, index) => index + 1,
      width: MIN_COLUMN_WIDTH,
      fixed: "left",
    },
    {
      title: translation("setting.ai.name"),
      dataIndex: "name",
      key: "name",
      width: 150,
    },
    {
      title: translation("setting.ai.keywords"),
      dataIndex: "keywords",
      key: "keywords",
      width: 200,
    },
    {
      title: translation("setting.ai.fullTextSearch"),
      dataIndex: "fullTextSearch",
      key: "fullTextSearch",
      width: EXTRA_EXTRA_BIG_COLUMND_WITDTH,
    },
    {
      title: translation("setting.ai.fullOriginContent"),
      dataIndex: "fullOriginContent",
      key: "fullOriginContent",
      width: EXTRA_EXTRA_BIG_COLUMND_WITDTH,
    },
    {
      title: translation("setting.ai.actions"),
      key: "action",
      width: BIG_COLUMN_WIDTH,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => openEditModal(record)}>
            Sửa
          </Button>
          <Popconfirm
            title={translation("setting.ai.confirmDelete")}
            onConfirm={() => handleDelete(record.wavdbId)}
            okText={translation("setting.ai.delete")}
            cancelText={translation("setting.ai.cancel")}
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger>
              {translation("setting.ai.delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Search function
  async function fetchData() {
    if (!filter.searchText) {
      return notification["warning"]({
        message: "",
        description: "Vui lòng nhập từ khoá để tìm kiếm",
      });
    }
    setLoading(true);
    const response = await AIAssistantService.searchExpertKnowledge(filter);
    setLoading(false);
    if (response.isSuccess) {
      setData(response.data.data || []);
    } else {
      setData([]);
    }
  }

  async function handleDelete(wavdbId) {
    if (loadingSubmit) return;
    setLoadingSubmit(true);
    const response = await AIAssistantService.deleteExpertKnowledge({
      merchantCode: filter.merchantCode,
      wavdbId: wavdbId,
    });
    setLoadingSubmit(false);
    if (response.isSuccess) {
      notification["success"]({
        message: "",
        description: translation("deleteSuccess"),
      });
      filter?.searchText && fetchData();
    } else {
      notification["error"]({
        message: "",
        description: translation("deleteFailed"),
      });
    }
  }

  function openEditModal(record) {
    setEditingRecord(record);
    form.setFieldsValue(record);
    setIsModalOpen(true);
  }

  function closeModal() {
    setIsModalOpen(false);
    setEditingRecord(null);
    form.resetFields();
  }

  async function handleUpdate(values) {
    if (loadingSubmit) return;
    setLoadingSubmit(true);
    const response = await AIAssistantService.updateExpertKnowledge({
      merchantCode: filter.merchantCode,
      wavdbId: editingRecord?.wavdbId,
      data: values,
    });
    setLoadingSubmit(false);
    if (response.isSuccess) {
      notification["success"]({
        message: "",
        description: translation("updateSuccess"),
      });
      filter?.searchText && fetchData();
      closeModal();
    } else {
      notification["error"]({
        message: "",
        description: translation("updateFailed"),
      });
    }
  }

  async function addNewExpertKnowledge(values) {
    if (loadingSubmit) return;
    setLoadingSubmit(true);
    const response = await AIAssistantService.addNewExpertKnowledge({
      merchantCode: filter.merchantCode,
      data: [values],
    });
    setLoadingSubmit(false);
    if (response.isSuccess) {
      notification["success"]({
        message: "",
        description: translation("createSuccess"),
      });
      filter?.searchText && fetchData();
      closeModal();
    } else {
      notification["error"]({
        message: "",
        description: translation("createFailed"),
      });
    }
  }

  // Modal submit: tự phân biệt add/update
  const handleSubmit = (values) => {
    if (editingRecord) {
      handleUpdate(values);
    } else {
      addNewExpertKnowledge(values);
    }
  };

  const handleSync = () => {
    if (loadingSync) return;
    setLoadingSync(true);
    TorichatService.syncKnowledge({}).then((result) => {
      setLoadingSync(false);
      if (result.isSuccess) {
        notification["success"]({
          message: "",
          description: "Đồng bộ thành công",
        });
      } else {
        notification["error"]({
          message: "",
          description: "Đồng bộ thất bại",
        });
      }
    });
  };

  return (
    <div>
      {/* Search bar */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={12} lg={4}>
          <Input.Search
            autoFocus
            placeholder={translation("setting.ai.search")}
            className="w-100"
            value={filter.searchText}
            onChange={(e) =>
              setFilter({ ...filter, searchText: e.target.value })
            }
            onSearch={(value) => {
              fetchData(value);
            }}
          />
        </Col>
        <Col>
          <BasicButtonPrimary
            onClick={() => {
              form.resetFields();
              setEditingRecord(null);
              setIsModalOpen(true);
            }}
            text={translation("setting.ai.add")}
          />
        </Col>
        <Col>
          <Popconfirm
            title={translation("setting.ai.syncParagraphDataFromCenter")+"?"}
            onConfirm={() => handleSync()}
            okText={translation("torichat.sync")}
            cancelText={translation("setting.ai.cancel")}
            okButtonProps={{ danger: true }}
          >
            <BasicButtonPrimary
              loading={loadingSync}
              text={translation("setting.ai.syncParagraphDataFromCenter")}
            />
          </Popconfirm>
        </Col>
      </Row>

      {/* Table */}
      <div>
        <Table
          className="mt-3"
          scroll={{ x: 1200 }}
          columns={columns}
          dataSource={data}
          pagination={false}
          rowKey="id"
          loading={loading}
        />
      </div>

      {/* Modal for Add/Edit */}
      <Modal
        title={
          editingRecord
            ? translation("setting.ai.updateKnowledge")
            : translation("setting.ai.addKnowledge")
        }
        visible={isModalOpen}
        onCancel={closeModal}
        footer={
          <>
            <BasicButtonPrimary
              loading={loadingSubmit}
              onclick={() => {
                form.submit();
              }}
              text={translation("setting.ai.save")}
            />
          </>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label={translation("setting.ai.name")}
            rules={[
              {
                required: true,
                message: translation("setting.ai.pleaseEnterName"),
              },
            ]}
          >
            <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} />
          </Form.Item>

          <Form.Item
            name="keywords"
            label={translation("setting.ai.keywords")}
            rules={[{ required: true, message: "Vui lòng nhập từ khóa" }]}
          >
            <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} />
          </Form.Item>
          <Form.Item
            name="fullTextSearch"
            label={translation("setting.ai.fullTextSearch")}
            rules={[
              { required: true, message: "Vui lòng nhập nội dung trả lời" },
            ]}
          >
            <Input.TextArea autoSize={{ minRows: 4, maxRows: 8 }} />
          </Form.Item>
          <Form.Item
            name="fullOriginContent"
            label={translation("setting.ai.fullOriginContent")}
            rules={[
              {
                required: true,
                message: "Vui lòng nhập nội dung gốc tham khảo",
              },
            ]}
          >
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 8 }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
